// @flow

import React, { type Node } from 'react';
import { useUserInputAsTextField } from '@tectonics/prs/user-input-values';
import { Label, TextField } from '../../../../../commons/atoms';
import { InputGroup } from '../../../../../commons/molecules';
import { useIncomingPayment, getCountry } from '@tectonics/prs/incoming-payment';
import { useUserInputValidationError, type UserInputValidationError } from '@tectonics/prs/validation';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';
import { useUniqueId, useMustShow } from '@tectonics/hooks';
import { ErrorLabel, InputHolder } from '../commons';
import { useSubmitting } from '@tectonics/prs/submitting';
import { type Country } from '@tectonics/prs/country';
import { translate } from '@tectonics/translate';

export function InputCustomerName(): Node {
  const mustShow = useMustShow('customerName');
  const incomingPayment = useIncomingPayment();
  const [customerName, handleCustomerNameChange] = useUserInputAsTextField('customerName');
  const inputId = useUniqueId();
  const submitting = useSubmitting();
  const formHasBeenSent = useFormHasBeenSent();
  const validationError = useUserInputValidationError('customerName');
  const country = getCountry(incomingPayment);

  if (!mustShow) {
    return null;
  }

  return (
    <InputHolder data-test-name="customer-name">
      <InputGroup
        label={
          <Label htmlFor={inputId}>
            <LabelMessage />
          </Label>}
        input={
          <TextField
            id={inputId}
            value={customerName}
            onChange={handleCustomerNameChange}
            fashion={formHasBeenSent && validationError ? 'error' : null}
            readOnly={submitting}
            data-error={formHasBeenSent && validationError ? 'error' : null}
            name="customer-name"
          />
        }
      />
      {formHasBeenSent && validationError && (
        <ValidationErrorMessage country={country} validationError={validationError} />
      )}
    </InputHolder>
  );
}

function LabelMessage(): Node {
  return translate('shopify.inputCustomerName');
}

type ValidationErrorMessageProps = {|
  country: Country,
  validationError: UserInputValidationError,
|};

const messages = (validationError: UserInputValidationError) => {
  switch (validationError.id) {
    case 'empty':
      return {
        br: 'Preencha o seu nome',
        mx: 'El nombre es requerido',
        ar: 'El nombre es requerido',
        co: 'El nombre es requerido',
        cl: 'El nombre es requerido',
        pe: 'El nombre es requerido',
        ec: 'El nombre es requerido',
        bo: 'El nombre es requerido',
        uy: 'El nombre es requerido',
        py: 'El nombre es requerido',
        cr: 'El nombre es requerido',
        pa: 'El nombre es requerido',
        sv: 'El nombre es requerido',
        gt: 'El nombre es requerido',
        do: 'El nombre es requerido',
        za: 'Please, insert your name',
        ke: 'Please, insert your name',
        ng: 'Please, insert your name',
        in: 'Please, insert your name',
        tz: 'Please, insert your name',
        ug: 'Please, insert your name',
        zm: 'Please, insert your name',
        eg: 'Please, insert your name',
        gh: 'Please, insert your name',
        jm: 'Please, insert your name',
      };

    default:
      return {
        br: 'Nome inválido',
        mx: 'Nombre inválido',
        ar: 'Nombre inválido',
        co: 'Nombre inválido',
        cl: 'Nombre inválido',
        pe: 'Nombre inválido',
        ec: 'Nombre inválido',
        bo: 'Nombre inválido',
        uy: 'Nombre inválido',
        py: 'Nombre inválido',
        pa: 'Nombre inválido',
        cr: 'Nombre inválido',
        sv: 'Nombre inválido',
        gt: 'Nombre inválido',
        do: 'Nombre inválido',
        za: 'Invalid name',
        ke: 'Invalid name',
        ng: 'Invalid name',
        tz: 'Invalid name',
        in: 'Invalid name',
        ug: 'Invalid name',
        zm: 'Invalid name',
        eg: 'Invalid name',
        gh: 'Invalid name',
        jm: 'Invalid name',
      };
  }
};

function ValidationErrorMessage({ country, validationError }: ValidationErrorMessageProps): Node {
  return (
    <ErrorLabel>
      {messages(validationError)[country] || validationError.payload}
    </ErrorLabel>
  );
}
